/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { CCard, CCardHeader, CCardBody } from '@coreui/react';
import { useDispatch, useSelector } from 'react-redux';
import Preloader from '../../../Shared/Preloader';
import { inviteUserByEmail, getDataForInviteForm } from './actions';
import InviteForm from '../Shared/InviteForm';
import useGlobalSelectionsContext from '../../../core/hooks/useGlobalSelectionsContext';
import useUserRoles from '../../../core/hooks/useUserRoles';
import useCanPermissions from '../../../core/hooks/useCanPermissions';
import permissions from '../../../core/configs/permissions';
import { fetchTeams } from '../../Teams/actions';
import { fetchLabels } from '../../Labels/UserLabelsList/actions';

const InviteByEmail = ({
  title = 'Invite user',
  history,
  onSubmit,
  Container = CCard,
  roles = [],
  withOnlyEmail = false,
}) => {
  const dispatch = useDispatch();
  // eslint-disable-next-line max-len
  const { loading, organizations, registrationForms } = useSelector(
    ({ users }) => users.inviteByEmail,
  );
  const { teams: teamsOptions } = useSelector(({ teams }) => teams);
  const { data: labelsOptions } = useSelector((state) => state.labels.list);

  const { filteredRolesOptions: hookRolesOptions } = useUserRoles(true);
  const filteredRolesOptions = roles.length ? roles : hookRolesOptions;
  const { initiativeId, adminMode, isOrganizationAdmin, organizationId } =
    useGlobalSelectionsContext();
  const { isCan } = useCanPermissions();

  const showTeams = React.useMemo(
    () =>
      isCan({
        permission: permissions.view_users_teams,
        initiativeMode: true,
        adminMode: false,
      }) && !withOnlyEmail,
    [isCan, withOnlyEmail],
  );

  const showLabels = React.useMemo(
    () =>
      isCan({
        permission: permissions.read_label,
        initiativeMode: true,
        adminMode: false,
      }) && !withOnlyEmail,
    [isCan, withOnlyEmail],
  );

  const showRegistrationForms = React.useMemo(
    () =>
      isCan({
        permission: permissions.read_registration_form,
        initiativeMode: true,
        adminMode: false,
      }) && !withOnlyEmail,
    [isCan, withOnlyEmail],
  );

  const showRoleSelect = (!isOrganizationAdmin || !adminMode) && filteredRolesOptions.length > 1;

  const handleSubmit = (data) => {
    dispatch(
      inviteUserByEmail({
        ...data,
        initiative_id: initiativeId,
        organization_id: organizationId,
        organizations: [{ id: organizationId, role: 'organization-user' }],
      }),
    ).then((newUser) => (history ? history.push('/users') : onSubmit(newUser)));
  };

  useEffect(() => {
    const params = {
      initiative_id: initiativeId,
      organization_id: organizationId,
      perPage: 999,
    };

    if (showTeams) dispatch(fetchTeams(initiativeId));
    if (showLabels) dispatch(fetchLabels(params));
    if (showRegistrationForms) dispatch(getDataForInviteForm(params));
    // eslint-disable-next-line
  }, [dispatch, initiativeId, showTeams]);

  return (
    <Container>
      {loading && <Preloader />}
      {title && (
        <CCardHeader>
          <h3>{title}</h3>
        </CCardHeader>
      )}
      <CCardBody>
        <InviteForm
          onSubmit={handleSubmit}
          organizationsOptions={organizations}
          rolesOptions={filteredRolesOptions}
          teamsOptions={teamsOptions}
          labelsOptions={labelsOptions}
          registrationFormsOptions={registrationForms}
          isLoading={loading}
          withEmail
          showTeams={showTeams}
          showLabels={showLabels}
          showRoleSelect={showRoleSelect}
          showRegistrationForms={showRegistrationForms}
        />
      </CCardBody>
    </Container>
  );
};

InviteByEmail.propTypes = {
  title: PropTypes.string,
  history: PropTypes.object,
  onSubmit: PropTypes.func,
  Container: PropTypes.func,
  roles: PropTypes.array,
  withOnlyEmail: PropTypes.bool,
};

InviteByEmail.defaultProps = {
  title: 'Invite user',
  history: null,
  onSubmit: () => {},
  Container: CCard,
  roles: [],
  withOnlyEmail: false,
};

export default InviteByEmail;
